/**
 * The phone app's strings (the "mobile" flavor, lib/config.ts isMobileApp;
 * docs/mobile/store-compliance.md D11, D14, S1, S7).
 *
 * `mobileEn` holds the keys of the phone app's own screens (the agreement to
 * the Terms, Settings > Help & Safety); it is in every build, as every key a
 * screen uses is. `mobileWordingEn` replaces web and desktop wording in the
 * mobile build only: no browser, tab, right-click, Windows or alpha, and tap
 * and long-press for click and right-click. Screens the phone app leaves out
 * (the updater, the Windows download, keybinds, game detection, Settings >
 * Server) keep their words; test/mobile-flavor.test.ts lists them.
 */
import type { Dict } from './core';
import { umEn } from './um';

export const mobileEn: Dict = {
  // ---- the Terms and the Community Guidelines (sign-up, and once per account)
  'mob.terms.agree': 'I agree to the {terms} and the {guidelines}.',
  'mob.terms.terms': 'Terms of Service',
  'mob.terms.guidelines': 'Community Guidelines',
  'mob.terms.zero': 'There is no tolerance for objectionable content or abusive users.',
  'mob.terms.title': 'Before you continue',
  'mob.terms.body':
    'To use {brand}, agree to the Terms of Service and the Community Guidelines. Content and people that break them are removed.',
  'mob.terms.accept': 'Agree and continue',

  // ---- Settings > Help & Safety
  'mob.help.title': 'Help & Safety',
  'mob.help.report': 'Report a problem',
  'mob.help.reportSub':
    'To report a message or a person, long-press the message or their name and choose Report. For anything else, write to us.',
  'mob.help.safety': 'Safety team',
  'mob.help.safetySub':
    'Abuse, threats, harm to children or anything that needs action quickly. We act on reports within 24 hours.',
  'mob.help.support': 'Support',
  'mob.help.supportSub': 'Questions about your account or the app.',
  'mob.help.pages': 'Policies',
  'mob.help.privacy': 'Privacy Policy',
  'mob.help.terms': 'Terms of Service',
  'mob.help.guidelines': 'Community Guidelines',
  'mob.help.childSafety': 'Child Safety Standards',
  'mob.help.deleteAccount': 'Deleting your account',
  'mob.help.supportPage': 'Help center',
  // a link the app couldn't open (lib/mobileGlue.ts)
  'mob.link.copied': "This couldn't be opened, so it was copied: {text}",

  // ---- Settings > Notifications: push, which the phone's own Settings turn off
  'mob.notif.phone':
    "This phone gets notifications for DMs, mentions and replies. To turn them on or off, open Notifications for Neosocial in your phone's Settings.",
};

/** "right-click your name" in the voice tips (um.voice.*): a long-press on a phone. */
const longPressTips: Dict = Object.fromEntries(
  Object.entries(umEn).flatMap(([k, v]) =>
    typeof v === 'string' && v.includes('right-click your name')
      ? [[k, v.replace('right-click your name', 'long-press your name')]]
      : [],
  ),
);

export const mobileWordingEn: Dict = {
  ...longPressTips,
  'hub.spoiler': 'Spoiler, tap to reveal',
  'nxs.note.placeholder': 'Tap to add a note',
  'pres.dnd.sub': "You won't get notifications",
  'set.theme.sub': 'Pearl follows the light, Smoked the dark. System matches your phone.',
  'auth.alpha.code': 'Invite code',
  'ns.auth.foot': 'Adults only (18+). No ID checks, ever.',
  'ns.cmp.imagesOnly': 'Only images can be uploaded for now.',
  // Settings > Devices: the kind of app, never a browser or a system name (D11)
  'ns.client.desktop': 'Desktop',
  'ns.client.android': 'Phone',
  'ns.client.ios': 'iPhone',
  // notifications come as push notifications here
  'ns.set.desktopNotif': 'Enable notifications',
  'ns.set.desktopNotifSub':
    'Notifications on this phone for DMs, mentions, replies, calls, pokes and friend requests.',
  'ns.set.webNotifSub':
    'Notifications on this phone for DMs, mentions, replies, calls, pokes and friend requests.',
  'ns.set.notifBlocked':
    "Notifications are turned off for Neosocial. Turn them on in your phone's Settings.",
  'set.notif.ask': "Notifications aren't allowed on this phone yet, so none are shown.",
  'set.notif.unsupported': "Notifications can't be shown here.",
  'ns.voice.noMic':
    "The microphone couldn't be opened. Allow Neosocial to use it in your phone's Settings.",
  'ns.voice.processing': "Echo cancellation and gain control use your phone's built-in processing.",
  'vx.ns.sub.standard': "Your phone's own noise suppression.",
  'vx.ns.unsupported': "Strong can't run on this phone, so Standard is used.",
  'stream.err.camBlocked':
    "Camera access is blocked. Allow Neosocial to use the camera in your phone's Settings.",
  'ns.copy.failed': "Copying wasn't allowed. Select it and copy it yourself: {text}",
  'ns.msg.copyFailed': "Couldn't copy to the clipboard.",
  'img.err.heic': "This photo couldn't be opened. Save it as JPEG or PNG and try again.",
  'um.recovery.waitingDevice':
    'Open {brand} on a device where you are signed in, and approve the request there. This screen continues by itself.',
};
